import React, { useState, useRef, useEffect } from 'react';
import { Leaf } from 'lucide-react';
import HeroSection from '../../components/landing/HeroSection';
import ProblemSection from '../../components/landing/ProblemSection';
import SocialProofSection from '../../components/landing/SocialProofSection';
import WhatsIncluded from '../../components/landing/WhatsIncluded';
import CTASection from '../../components/landing/CTASection';
import EmailCaptureForm from '../../components/landing/EmailCaptureForm';
import { MEDIA_ITEMS, TESTIMONIALS, PRODUCT_INFO, ProductDetails } from './constants';

export default function StartGrowingLanding() {
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const emailSectionRef = useRef<HTMLDivElement>(null);

  const product: ProductDetails = {
    id: PRODUCT_INFO['The Grow Box'].slug,
    name: 'The Grow Box',
    price: PRODUCT_INFO['The Grow Box'].price,
    slug: PRODUCT_INFO['The Grow Box'].slug,
    external_url: PRODUCT_INFO['The Grow Box'].external_url,
    description: 'Everything you need to start growing'
  };

  useEffect(() => {
    if (showEmailCapture && emailSectionRef.current) {
      setTimeout(() => {
        emailSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [showEmailCapture]);

  const handleCtaClick = () => {
    setShowEmailCapture(true);
  };

  const problems = [
    "You overwater. Or underwater. Never quite right.",
    "The light is wrong. Always.",
    "Too much guessing. Not enough growing.",
    "Your plants die anyway."
  ];

  const whatsIncludedItems = [
    {
      icon: 'box' as const,
      title: 'Grow Bag',
      description: '10-gallon fabric grow bag, perfect for any space'
    },
    {
      icon: 'sprout' as const,
      title: 'Organic Seeds',
      description: 'Zone-matched seed selection tailored to you'
    },
    {
      icon: 'droplets' as const,
      title: 'Watering Spike',
      description: 'Self-watering system for easy maintenance'
    },
    {
      icon: 'package' as const,
      title: 'Fertilizer',
      description: 'Organic nutrients for healthy growth'
    },
    {
      icon: 'filetext' as const,
      title: 'Step-By-Step Guide',
      description: 'Personalized planting plan for your zone'
    },
    {
      icon: 'video' as const,
      title: 'Blooma App Support',
      description: 'Video tutorials and growing tips'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      {/* Hero Section */}
      <HeroSection
        headline="Tired of Killing Every Plant You Touch?"
        subheadline="Grow fresh greens with zero guesswork — in just 5 minutes a week"
        ctaText="Build My Grow Box"
        ctaAction={handleCtaClick}
        mediaItems={MEDIA_ITEMS}
        showMediaCarousel={true}
      />

      {/* Problem Validation */}
      <ProblemSection
        problems={problems}
        empathyStatement="It's not you. It's the system."
        title="Sound Familiar?"
      />

      {/* Solution = Blooma */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Meet Your New Growing System
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="p-6">
                <div className="text-4xl mb-3">📦</div>
                <h3 className="font-bold text-xl mb-2">All-in-one</h3>
                <p className="text-gray-600">
                  Everything you need in one box. No hunting for supplies.
                </p>
              </div>
              <div className="p-6">
                <div className="text-4xl mb-3">✅</div>
                <h3 className="font-bold text-xl mb-2">No guesswork</h3>
                <p className="text-gray-600">
                  Step-by-step guidance matched to your growing zone.
                </p>
              </div>
              <div className="p-6">
                <div className="text-4xl mb-3">⏱️</div>
                <h3 className="font-bold text-xl mb-2">5 minutes a week</h3>
                <p className="text-gray-600">
                  Low maintenance. High reward. Perfect for busy lives.
                </p>
              </div>
            </div>
            <div className="max-w-2xl mx-auto">
              <img
                src="/Cool Veggie Garden Mock Up.png"
                alt="Blooma Grow Box lifestyle"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <WhatsIncluded
        items={whatsIncludedItems}
        setupTime="Sets up in under 10 minutes"
      />

      {/* Social Proof */}
      <SocialProofSection
        testimonials={TESTIMONIALS.slice(0, 3)}
        columns={3}
      />

      {/* Email Capture Section */}
      {showEmailCapture && (
        <section ref={emailSectionRef} className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-md mx-auto bg-white border-2 border-primary-200 rounded-lg p-6">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Last step! Let's get started
                </h3>
                <p className="text-gray-600">
                  Enter your email to continue to checkout
                </p>
              </div>
              <EmailCaptureForm
                product={product}
                sourcePage="start-growing"
                seedPackage="herb_garden"
                growingEnvironment="indoor"
              />
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      {!showEmailCapture && (
        <CTASection
          headline="Ready to Grow Fresh Greens at Home?"
          subheadline="No green thumb required."
          ctaText="Build My Grow Box"
          ctaAction={handleCtaClick}
        />
      )}
    </div>
  );
}
